"use client";

import Cookies from "js-cookie";
import { useRouter } from "next/router";
import { clearLastKnownAuthToken } from "queries/constants";
import { useCurrentUser } from "queries/current-user";
import { useCallback, useMemo } from "react";
import { SESSION_KEY } from "ui/lib/constants";
import { isDev } from "ui/lib/helpers";
import { forgetTeam } from "apollo/lib/last-team";
import { SECTION_NAMES, TOOL_NAMES } from "apollo/lib/strings";

export const sectionLinks = [
  { name: SECTION_NAMES.calendar, href: "overview" },
  {
    name: SECTION_NAMES.chat,
    // The full inbox, unread first; flags are a chip beside search.
    href: "chat",
    onlyCurrentCoachesTeam: true,
  },
  { name: SECTION_NAMES.movements, href: "movements" },
  { name: SECTION_NAMES.details, href: "details", adminOnly: true },
];

export const useSectionLinks = () => {
  const { isAdmin, isCoachesTeam } = useCurrentUser();

  return useMemo(
    () =>
      sectionLinks.filter(({ adminOnly, onlyCurrentCoachesTeam }) => {
        if (isAdmin) return true;
        if (adminOnly) return false;
        if (onlyCurrentCoachesTeam && !isCoachesTeam) return false;
        return true;
      }),
    [isAdmin, isCoachesTeam]
  );
};

// Section hrefs carry a query string; the pathname segment is what marks one active.
export const sectionSegment = (href) => href.split("?")[0];

export const toolLinks = [
  { name: TOOL_NAMES.blocks, href: "/blocks", coachVisible: true },
  { name: TOOL_NAMES.openSeason, href: "/open-season" },
  { name: TOOL_NAMES.curatedRooms, href: "/curated-rooms" },
  { name: TOOL_NAMES.homeShelves, href: "/home-shelves" },
  { name: TOOL_NAMES.remoteConfig, href: "/remote-config" },
  { name: TOOL_NAMES.ladderLinks, href: "/links/builder" },
  { name: TOOL_NAMES.promos, href: "/promos" },
  {
    name: TOOL_NAMES.partnerships,
    href: `${isDev ? "https://dev.joinladder.com" : "https://www.joinladder.com"}/app/internal/partnerships`,
    external: true,
  },
  { name: TOOL_NAMES.badges, href: "/badges" },
];

export const useToolLinks = () => {
  const { isAdmin, isCoach } = useCurrentUser();

  return useMemo(() => {
    if (isAdmin) return toolLinks;
    if (isCoach) return toolLinks.filter(({ coachVisible }) => coachVisible);
    return [];
  }, [isAdmin, isCoach]);
};

export const useSignOut = () => {
  const { push } = useRouter();

  return useCallback(() => {
    Cookies.remove(SESSION_KEY);
    // SPA navigation, no reload — clear the axios client's in-memory token
    // fallback or this tab keeps authenticating after logout.
    clearLastKnownAuthToken();
    forgetTeam();
    push("/", "/");
  }, [push]);
};
